/*
  Awarahun motion — plays each reveal once as it scrolls into view.
  Styles live in assets/aw-motion.css; this only adds .is-in (start) and .is-done
  (finished — the reveal transition is removed so hovers keep their own timing).
*/
/*
  Photos further down start downloading about two screens ahead of the reader, instead
  of the browser's own (later) lazy-loading point, so they are ready when scrolled to.
  This begins after the page has loaded.
  The files themselves are unchanged (same size and quality).
*/
(function () {
  if (!('IntersectionObserver' in window)) return;
  const early = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        early.unobserve(entry.target);
        (photos.get(entry.target) || []).forEach((img) => (img.loading = 'eager'));
      });
    },
    { rootMargin: '200% 0px 200% 0px' }
  );
  // A photo still hidden by its reveal (clip-path) measures as a thin line, so photos
  // inside a reveal are watched through the element around it.
  const photos = new Map();
  const watchLazy = () =>
    document.querySelectorAll('main img[loading="lazy"]').forEach((img) => {
      const box = img.closest('.aw-rv-img');
      const watched = (box && box.parentElement) || img;
      if (!photos.has(watched)) {
        photos.set(watched, []);
        early.observe(watched);
      }
      photos.get(watched).push(img);
    });
  // Only once the page has loaded, so the photos on screen get the connection first.
  if (document.readyState === 'complete') watchLazy();
  else window.addEventListener('load', watchLazy, { once: true });
})();

(function () {
  const root = document.documentElement;
  window.__awMotion = true;
  if (!root.classList.contains('aw-motion')) return;
  if (!('IntersectionObserver' in window)) {
    root.classList.remove('aw-motion');
    return;
  }

  const TARGETS = [
    '.aw-rv',
    '.aw-rv-img',
    '.aw-illo',
    '.aw-products__grid > *',
    '.aw-collection__grid > *',
    '.aw-values__list > *',
  ].join(',');
  // Longest reveal (photo zoom, 1.6s) plus a margin; stagger is added per element
  const FINISH_MS = 1800;
  const STEP_MS = 90;

  // Photos wipe in only once the picture is ready, so the wipe never shows an empty box.
  // A slow photo is revealed anyway after IMAGE_WAIT_MS (it then fills in as it arrives).
  const IMAGE_WAIT_MS = 700;
  function whenImageReady(el, done) {
    const img = el.tagName === 'IMG' ? el : el.querySelector('img');
    if (!img || (img.complete && img.naturalWidth > 0)) return done();
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      done();
    };
    img.addEventListener('load', () => (img.decode ? img.decode().catch(() => {}).then(finish) : finish()), { once: true });
    img.addEventListener('error', finish, { once: true });
    window.setTimeout(finish, IMAGE_WAIT_MS);
  }

  function reveal(el, order) {
    // Started early by its own onload (first photo on the page): just finish it
    if (el.classList.contains('is-in')) {
      if (!el.classList.contains('is-done')) window.setTimeout(() => el.classList.add('is-done'), FINISH_MS);
      return;
    }
    if (el.classList.contains('aw-rv-img') && !el.dataset.awReady) {
      el.dataset.awReady = '1';
      whenImageReady(el, () => reveal(el, order));
      return;
    }
    el.style.setProperty('--rv-order', order);
    el.classList.add('is-in');
    const lines = el.classList.contains('aw-rv') ? el.children.length : 0;
    window.setTimeout(() => {
      el.classList.add('is-done');
      // Text has settled into its final place: drawings re-check they stay clear of it
      document.dispatchEvent(new CustomEvent('aw:revealed'));
    }, FINISH_MS + (order + lines) * STEP_MS);
  }

  /*
    What each observed element reveals. Usually just itself. A photo (.aw-rv-img) is
    hidden with clip-path, and browsers measure a clipped element by its clipped area:
    a thin line at the photo's bottom edge. A tall photo would then reveal only once
    its bottom scrolled into view. So photos are watched through their section instead
    and reveal together with the section's text.
  */
  const reveals = new Map();
  function watch(el) {
    const watched = el.classList.contains('aw-rv-img') && el.parentElement ? el.parentElement : el;
    if (!reveals.has(watched)) {
      reveals.set(watched, []);
      observer.observe(watched);
    }
    reveals.get(watched).push(el);
  }

  let heard = false;
  const observer = new IntersectionObserver(
    (entries) => {
      heard = true;
      // On screen now, or already scrolled past (e.g. page reloaded half-way down)
      const arriving = entries
        .filter((entry) => entry.isIntersecting || entry.boundingClientRect.bottom < 0)
        .flatMap((entry) => {
          observer.unobserve(entry.target);
          const targets = reveals.get(entry.target) || [];
          reveals.delete(entry.target);
          return targets;
        });
      // Cards that arrive together come in reading order: left to right, top to bottom
      arriving.sort((a, b) => {
        const ra = a.getBoundingClientRect();
        const rb = b.getBoundingClientRect();
        return Math.round(ra.top - rb.top) || ra.left - rb.left;
      });
      let order = 0;
      arriving.forEach((el) => {
        const isItem = !el.matches('.aw-rv, .aw-rv-img, .aw-illo');
        reveal(el, isItem ? Math.min(order++, 5) : 0);
      });
    },
    { rootMargin: '0px 0px -4% 0px', threshold: 0 }
  );

  function arm(scope) {
    scope.querySelectorAll(TARGETS).forEach((el) => {
      if (el.closest('.aw-pairing')) return;
      // Already started by its own onload: finish it off (reveal transition removed)
      if (el.classList.contains('is-in')) {
        window.setTimeout(() => el.classList.add('is-done'), FINISH_MS);
        return;
      }
      // Lines of a text block arrive one after another
      if (el.classList.contains('aw-rv')) {
        [...el.children].forEach((child, index) => child.style.setProperty('--rv-i', index));
      }
      watch(el);
    });
  }

  arm(document);

  // Safety net: the observer always reports once a visible page renders. If it
  // stays silent (an embedded or unusual browser), show everything rather than
  // leave content hidden.
  const safetyNet = () =>
    window.setTimeout(() => {
      if (heard || document.visibilityState !== 'visible') return;
      observer.disconnect();
      document.querySelectorAll(TARGETS).forEach((el) => el.classList.add('is-in', 'is-done'));
    }, 2500);
  safetyNet();
  // Opened in a background tab: check again once it is looked at
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') safetyNet();
  });

  // Theme editor: re-rendered sections show straight away
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll(TARGETS).forEach((el) => el.classList.add('is-in', 'is-done'));
  });
})();
